/**
 * projectiles.ts — the guns' PROJECTILES (combat wave, 2026-09-05; made
 * VISIBLE 2026-09-07 — "I still do not see a projectile come out of my gun").
 *
 * Purely visual: the server judges every shot on the rows the instant it is
 * fired; this draws the shot travelling from the muzzle to the mark. The first
 * cut drew a 5 cm box at 180–320 m/s — three to five metres per frame,
 * receding straight down the view axis, additively blended: a flicker at best,
 * nothing against the sky. What an FPS actually shows (and what this draws):
 *   · a MUZZLE FLASH the eye cannot miss — a camera-facing glow quad at the
 *     barrel, ~0.4 m, gone in 70 ms;
 *   · a TRACER: a bright camera-facing HEAD dot plus a STREAK quad behind it
 *     (a linear gradient, head-bright to tail-clear) oriented along the flight
 *     and turned to face the camera every frame — from first person it reads
 *     as a converging line from the barrel to the crosshair;
 *   · a VISUAL speed cap (VISUAL_SPEED_MAX): the server already judged the
 *     shot, so the drawing may take the ~¼ s a player can follow;
 *   · an IMPACT burst at the mark.
 * The lance stays an instant beam (a wide streak from muzzle to mark).
 *
 * Pooled: two shared plane geometries, one material per (texture, colour),
 * quads recycled from free lists, never disposed per shot (the GPU shield
 * law: nothing ever drawn is destroyed).
 */
import * as THREE from 'three/webgpu';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';
import { materialOpacity } from 'three/tsl';
import { applySoftParticles } from 'enginex/vfx/SoftParticleModifier.js';
import { activePreset } from './quality_tiers.js';
import { softHandles, stage3Stats } from './stage3_fx.js';

export interface ProjectileSpec { kind: 'slug' | 'bolt' | 'pellets' | 'shell' | 'ring' | 'lance'; speedMps: number; color: number; size: number }

type Shape = 'dot' | 'streak';

interface Live {
  head: THREE.Mesh;
  trail: THREE.Mesh | null;
  from: THREE.Vector3; to: THREE.Vector3; dir: THREE.Vector3;
  dist: number; travelled: number; speed: number;
  kind: ProjectileSpec['kind'];
  /** World size of the head (dot diameter) / the flash or burst quad. */
  size: number;
  /** Streak length + width (m) for the trail. */
  trailLen: number; trailW: number;
  /** ms of life for timed shapes (flash / burst / lance). */
  ttl: number; age: number;
  onImpact: (() => void) | null;
  /**
   * Did this shot actually MEET something at `to`?
   *
   * False for a miss flying out to the visual range: it reaches the end of its
   * flight and simply stops being drawn — no burst, no impact sound, no mark in
   * open air (2026-09-15).
   */
  hits: boolean;
  beam: boolean;
}

export const LANCE_MS = 130;
export const FLASH_MS = 70;
export const BURST_MS = 160;
export const MAX_LIVE = 96;
/** The eye follows ~75 m/s: an 18 m pistol shot lasts a quarter second. */
export const VISUAL_SPEED_MAX = 75;
/**
 * Smallest the bolt may draw ON SCREEN, in pixels of the DRAWING BUFFER.
 *
 * A tracer is a 13 cm-wide quad. At the internal resolutions the cheap tiers
 * actually rasterise at — FSR performance renders 458×240 before upscaling —
 * that is under a pixel at anything past a few metres, so the shot simply was
 * not there (2026-09-15, "I do not see it anymore"). Rasterisation happens at
 * the DRAWING BUFFER's size, so that is the resolution the floor is measured
 * in: winning it back after the upscale is too late.
 *
 * This widens the DRAWING only. Atlas's projectiles carry no collider — the
 * server judges the strike on the rows — so nothing about the shot's reach or
 * damage changes with it.
 */
export const MIN_SCREEN_PX = 2.6;
/**
 * Absolute ceiling (m) on a quad widened by the screen floor.
 *
 * Capping by a MULTIPLE of the authored size defeats the floor exactly where it
 * is needed: a 13 cm tracer at 400 m needs to be 5 m wide to hold 2.6 px, which
 * a 9× cap refuses — so the distant bolt went sub-pixel anyway. What actually
 * needs bounding is the world geometry, not the ratio: the quad is
 * camera-facing and additive, so a wide quad far away looks identical to a
 * narrow one near by. 12 m holds the floor past 400 m for a tracer while
 * staying far too small to intersect anything near the camera.
 */
const MAX_FLOOR_WORLD_M = 12;
/**
 * How far a shot that hits NOTHING keeps flying, in metres.
 *
 * A bolt used to stop dead at the weapon's damage range — a shot into the open
 * sky just ended in mid-air. A miss should carry on until it meets something
 * or leaves the frame.
 */
export const VISUAL_RANGE_M = 1400;

/**
 * The world size a quad must have to cover at least MIN_SCREEN_PX on screen —
 * the visibility floor, as a pure function so the harness can pin it.
 *
 * A world size `s` at distance `d` covers `s / (2·d·tan(fov/2)) · H` pixels.
 * Solving for the smallest `s` that reaches the floor gives the value below;
 * it is only ever applied as a MINIMUM, and never more than MAX_SCREEN_BOOST×
 * the authored size (a floor, not a licence to paint the sky).
 */
export function minScreenWorldSize(size: number, dist: number, tanHalfFov: number, viewportH: number): number {
  if (!(viewportH > 0) || !(dist > 1e-4) || !(tanHalfFov > 0)) return size;
  const need = (MIN_SCREEN_PX * 2 * dist * tanHalfFov) / viewportH;
  return need <= size ? size : Math.max(size, Math.min(need, MAX_FLOOR_WORLD_M));
}

/** On-screen diameter (px) of a world size at a distance — the mirror of the above. */
export function screenPxFor(size: number, dist: number, tanHalfFov: number, viewportH: number): number {
  if (!(dist > 1e-4) || !(tanHalfFov > 0)) return 0;
  return (size / (2 * dist * tanHalfFov)) * viewportH;
}
/** Head dot diameter (m) at size multiplier 1 (a 5 cm catalogue slug). */
export const HEAD_M = 0.26;
/** Streak behind the head (m) at size multiplier 1. */
export const TRACER_LEN_M = 2.4;
export const TRACER_W_M = 0.13;
export const FLASH_M = 0.34;
export const BURST_M = 0.9;

function makeDotTexture(): THREE.Texture {
  const c = document.createElement('canvas');
  c.width = c.height = 64;
  const g = c.getContext('2d')!;
  const grad = g.createRadialGradient(32, 32, 0, 32, 32, 32);
  grad.addColorStop(0, 'rgba(255,255,255,1)');
  grad.addColorStop(0.28, 'rgba(255,255,255,0.9)');
  grad.addColorStop(0.6, 'rgba(255,255,255,0.28)');
  grad.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = grad;
  g.fillRect(0, 0, 64, 64);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

/** A streak: bright at +X (the head end), clear at −X (the tail); soft across. */
function makeStreakTexture(): THREE.Texture {
  const c = document.createElement('canvas');
  c.width = 128; c.height = 16;
  const g = c.getContext('2d')!;
  const along = g.createLinearGradient(0, 0, 128, 0);
  along.addColorStop(0, 'rgba(255,255,255,0)');
  along.addColorStop(0.55, 'rgba(255,255,255,0.35)');
  along.addColorStop(1, 'rgba(255,255,255,1)');
  g.fillStyle = along;
  g.fillRect(0, 0, 128, 16);
  // soft edges across the streak
  const across = g.createLinearGradient(0, 0, 0, 16);
  across.addColorStop(0, 'rgba(0,0,0,1)');
  across.addColorStop(0.5, 'rgba(0,0,0,0)');
  across.addColorStop(1, 'rgba(0,0,0,1)');
  g.globalCompositeOperation = 'destination-out';
  g.fillStyle = across;
  g.fillRect(0, 0, 128, 16);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

export class ProjectileField {
  readonly root = new THREE.Group();
  private readonly geo = new THREE.PlaneGeometry(1, 1);
  private readonly tex: Record<Shape, THREE.Texture> | null;
  private readonly mats = new Map<string, THREE.MeshBasicMaterial>();
  private readonly free = new Map<string, THREE.Mesh[]>();
  private readonly live: Live[] = [];
  /** The scene the quads hang under — it carries the FP64 origin rebase. */
  private readonly scene: THREE.Scene;
  /** Materials owned by ONE timed quad each (they animate their own opacity). */
  private readonly ownMats = new Set<THREE.Material>();
  readonly counters = { fired: 0, impacts: 0, live: 0 };
  private camera: THREE.Object3D | null = null;
  /** Probe lever: a frozen field keeps every shot where it is (screenshots). */
  frozen = false;
  /** Drawing-buffer height (px) and vertical FOV (rad) — the screen-size floor. */
  private _viewportH = 0;
  private _tanHalfFov = Math.tan((60 * Math.PI) / 360);
  private readonly _cam = new THREE.Vector3();
  private readonly _x = new THREE.Vector3();
  private readonly _y = new THREE.Vector3();
  private readonly _z = new THREE.Vector3();
  private readonly _p = new THREE.Vector3();
  private readonly _m = new THREE.Matrix4();
  private readonly _q = new THREE.Quaternion();
  private readonly _s = new THREE.Vector3();

  constructor(scene: THREE.Scene) {
    this.scene = scene;
    this.root.name = 'projectiles';
    scene.add(this.root);
    // (headless harnesses bundle this lib into node: no document → untextured quads)
    this.tex = typeof document !== 'undefined' ? { dot: makeDotTexture(), streak: makeStreakTexture() } : null;
  }

  /** The camera the streaks face (the first-person camera under the rig). */
  setCamera(cam: THREE.Object3D | null): void { this.camera = cam; }

  /** Build one projectile material (shared or owned — see `take`). */
  private makeMat(shape: Shape, color: number): THREE.MeshBasicMaterial {
    const params = {
      color, map: this.tex ? this.tex[shape] : null, transparent: true, opacity: 1,
      blending: THREE.AdditiveBlending, depthWrite: false, toneMapped: false, side: THREE.DoubleSide,
    };
    // Stage 3: SOFT particles — the flash/tracer/burst quads fade where they
    // cut a surface (depth difference), so a tracer skimming a wall or the
    // ground shows no hard slice (a node material: the modifier sets opacityNode).
    if (activePreset().vfxShaders) {
      const nm = new THREE.MeshBasicNodeMaterial(params as never);
      try {
        // KEEP THE CLASSIC `opacity` ALIVE. applySoftParticles MULTIPLIES any
        // existing opacityNode, and a NodeMaterial ignores `material.opacity`
        // once one is set — so without this the timed quads' fade (flash,
        // impact burst, lance beam) silently stopped animating on every tier
        // with vfxShaders on, and they popped out instead of fading.
        (nm as unknown as { opacityNode: unknown }).opacityNode = materialOpacity;
        const far = (this.camera as THREE.PerspectiveCamera | null)?.far ?? 1000;
        softHandles.add(applySoftParticles(nm as never, { softDistance: 0.35, cameraFar: far }));
        stage3Stats.soft++;
      } catch { /* best-effort */ }
      return markMrtOverlay(nm) as unknown as THREE.MeshBasicMaterial;
    }
    return markMrtOverlay(new THREE.MeshBasicMaterial(params)); // tracers/flashes/bursts over the world
  }

  /** The SHARED material for tracer quads — nothing ever writes to it. */
  private mat(shape: Shape, color: number): THREE.MeshBasicMaterial {
    const key = `${shape}:${color}`;
    let m = this.mats.get(key);
    if (!m) { m = this.makeMat(shape, color); this.mats.set(key, m); }
    return m;
  }

  /**
   * A pooled quad. `own` gives it a PRIVATE material.
   *
   * A timed quad animates `material.opacity`, and a muzzle flash and a tracer
   * head are both `dot` + the shot's colour — i.e. the same cached material.
   * So the flash's 70 ms fade (and every 160 ms impact burst) was driving the
   * shared opacity to 0 and blinking out every bolt of that colour in flight.
   * Owned quads pool under their own key so they keep their material on reuse;
   * the count is bounded by the pool, so nothing is ever created per shot.
   */
  private take(shape: Shape, color: number, own = false): THREE.Mesh {
    const key = own ? `own:${shape}:${color}` : `${shape}:${color}`;
    const pool = this.free.get(key);
    let m = pool?.pop();
    if (!m) {
      const mat = own ? this.makeMat(shape, color) : this.mat(shape, color);
      if (own) this.ownMats.add(mat);
      m = new THREE.Mesh(this.geo, mat);
    }
    (m.userData as { key: string }).key = key;
    if (own) (m.material as THREE.Material & { opacity: number }).opacity = 1;
    m.frustumCulled = false;
    m.visible = true;
    m.renderOrder = 15;
    this.root.add(m);
    return m;
  }

  private give(m: THREE.Mesh): void {
    this.root.remove(m);
    m.visible = false;
    const key = (m.userData as { key: string }).key;
    let pool = this.free.get(key);
    if (!pool) { pool = []; this.free.set(key, pool); }
    if (pool.length < 48) pool.push(m);
  }

  private release(l: Live): void {
    this.give(l.head);
    if (l.trail) this.give(l.trail);
  }

  /** Size multiplier from the catalogue size (a 5 cm slug = 1). */
  private static mul(spec: ProjectileSpec): number {
    return Math.max(0.8, Math.min(3, spec.size / 0.05));
  }

  /** Fire one shot from `from` to `to` (world space). Pellets fan out; a lance
   *  is instant. `onImpact` fires when the shot arrives (the burst + sound). */
  /**
   * Draw one shot from `from` to `to`.
   *
   * `hits` says whether `to` is a real contact (a surface or a body) or just
   * how far the bolt was allowed to fly. A miss still travels — out to
   * VISUAL_RANGE_M — but arrives at nothing.
   */
  fire(spec: ProjectileSpec, from: THREE.Vector3, to: THREE.Vector3, onImpact: (() => void) | null = null, hits = true): void {
    this.counters.fired++;
    const mul = ProjectileField.mul(spec);
    // Muzzle flash at the start of every shot.
    this.spawnTimed('dot', spec.color, from, FLASH_M * (0.75 + mul * 0.25), FLASH_MS);
    if (spec.kind === 'lance') {
      const l = this.spawnTimed('streak', spec.color, from, TRACER_W_M * 2.2, LANCE_MS, true);
      l.from.copy(from); l.to.copy(to); l.dir.subVectors(to, from).normalize();
      l.trailLen = from.distanceTo(to); l.trailW = TRACER_W_M * 2.2;
      // The beam lands only if it MET something — a lance fired into the sky
      // draws its full length and ends at nothing, like every other miss.
      if (hits) {
        this.spawnTimed('dot', spec.color, to, BURST_M * 0.9, BURST_MS);
        this.counters.impacts++;
        onImpact?.();
      }
      this.update(0);
      return;
    }
    const n = spec.kind === 'pellets' ? 6 : 1;
    const speed = Math.max(20, Math.min(spec.speedMps, VISUAL_SPEED_MAX));
    for (let i = 0; i < n; i++) {
      const target = to.clone();
      if (n > 1) { // a shotgun fan: scatter the marks around the aim point
        target.x += (Math.random() - 0.5) * 1.2; target.y += (Math.random() - 0.5) * 0.9; target.z += (Math.random() - 0.5) * 1.2;
      }
      const head = this.take('dot', spec.color);
      const trail = this.take('streak', spec.color);
      const dist = from.distanceTo(target);
      const headM = HEAD_M * (spec.kind === 'ring' ? 1.6 : 1) * mul;
      const l: Live = {
        head, trail, from: from.clone(), to: target, dir: target.clone().sub(from).normalize(), dist, travelled: 0, speed,
        kind: spec.kind, size: headM, trailLen: Math.min(TRACER_LEN_M * (0.7 + mul * 0.3), Math.max(0.6, dist)), trailW: TRACER_W_M * (0.7 + mul * 0.3),
        ttl: 0, age: 0, onImpact: i === 0 ? onImpact : null, beam: false, hits,
      };
      this.push(l);
    }
    this.update(0); // seat every quad THIS frame — a shot must never wait a frame to appear
  }

  private spawnTimed(shape: Shape, color: number, at: THREE.Vector3, size: number, ttl: number, beam = false): Live {
    const mesh = this.take(shape, color, true); // owns its material: it fades itself
    const l: Live = {
      head: mesh, trail: null, from: at.clone(), to: at.clone(), dir: new THREE.Vector3(0, 0, -1), dist: 0, travelled: 0, speed: 0,
      kind: 'bolt', size, trailLen: 0, trailW: size, ttl, age: 0, onImpact: null, beam, hits: true,
    };
    // Seat it NOW: a burst spawned inside update() is appended past the loop's
    // cursor and would draw one frame at the world origin otherwise.
    if (!beam) this.seatDot(mesh, at, size, this.cameraPos());
    this.push(l);
    return l;
  }

  private push(l: Live): void {
    if (this.live.length >= MAX_LIVE) { const old = this.live.shift()!; this.release(old); }
    this.live.push(l);
    this.counters.live = this.live.length;
  }

  /**
   * Tell the field what it is rasterising into, so the on-screen size floor is
   * measured in real pixels. Height is the DRAWING BUFFER's, not the CSS box's.
   */
  setViewport(drawingBufferHeight: number, verticalFovDeg: number): void {
    this._viewportH = Math.max(0, drawingBufferHeight);
    this._tanHalfFov = Math.tan((Math.max(1, verticalFovDeg) * Math.PI) / 360);
  }

  /**
   * Grow a world size so the quad covers at least MIN_SCREEN_PX.
   *
   * A world size `s` at distance `d` covers `s / (2·d·tan(fov/2)) · H` pixels,
   * so the smallest world size that reaches the floor is that solved for `s`.
   * Bounded by MAX_SCREEN_BOOST: this is a visibility floor, not a licence to
   * paint a bolt across the sky at a kilometre.
   */
  private minScreenSize(size: number, pos: THREE.Vector3, cam: THREE.Vector3): number {
    return minScreenWorldSize(size, cam.distanceTo(pos), this._tanHalfFov, this._viewportH);
  }

  /**
   * The camera in ABSOLUTE world coordinates.
   *
   * THE FP64 REBASE. The quads hang under the scene, and `scene.position` is
   * MINUS the current origin, so a scene child's `position` is an ABSOLUTE
   * coordinate. `getWorldPosition` returns the RENDER-space point (absolute −
   * origin), so it has to be brought back into the same frame — otherwise every
   * distance, every billboard basis and the on-screen size floor are wrong by
   * the whole origin the moment the player is more than 10 km from it.
   */
  private cameraPos(): THREE.Vector3 {
    if (this.camera) {
      this.camera.getWorldPosition(this._cam);
      this._cam.sub(this.scene.position);
    }
    return this._cam;
  }

  /** A camera-facing dot of diameter `size` at `pos`. */
  private seatDot(mesh: THREE.Mesh, pos: THREE.Vector3, size: number, cam: THREE.Vector3): void {
    size = this.minScreenSize(size, pos, cam);
    mesh.position.copy(pos);
    this._z.subVectors(cam, pos);
    if (this._z.lengthSq() < 1e-8) this._z.set(0, 0, 1);
    this._z.normalize();
    // up-ish reference → a stable basis
    this._y.set(0, 1, 0);
    if (Math.abs(this._z.dot(this._y)) > 0.98) this._y.set(1, 0, 0);
    this._x.crossVectors(this._y, this._z).normalize();
    this._y.crossVectors(this._z, this._x).normalize();
    this._m.makeBasis(this._x, this._y, this._z);
    mesh.quaternion.setFromRotationMatrix(this._m);
    mesh.scale.set(size, size, 1);
  }

  /** A streak of `len` × `w` whose +X runs along `dir`, its face turned to the camera, centred at `centre`. */
  private seatStreak(mesh: THREE.Mesh, centre: THREE.Vector3, dir: THREE.Vector3, len: number, w: number, cam: THREE.Vector3): void {
    // Only the WIDTH gets the floor: a streak's length is its exposure, and
    // stretching that would turn a tracer into a beam.
    w = this.minScreenSize(w, centre, cam);
    mesh.position.copy(centre);
    this._x.copy(dir);
    this._z.subVectors(cam, centre);
    // remove the along-flight component so the face turns to the eye without shearing the length
    this._z.addScaledVector(this._x, -this._z.dot(this._x));
    if (this._z.lengthSq() < 1e-6) { this._z.set(0, 1, 0); this._z.addScaledVector(this._x, -this._z.dot(this._x)); }
    this._z.normalize();
    this._y.crossVectors(this._z, this._x).normalize();
    this._m.makeBasis(this._x, this._y, this._z);
    mesh.quaternion.setFromRotationMatrix(this._m);
    mesh.scale.set(len, w, 1);
  }

  /** Advance every shot; call once per frame with the frame's dt (seconds). */
  update(dt: number): void {
    if (this.frozen) dt = 0;
    const ms = dt * 1000;
    const cam = this.cameraPos();
    for (let i = this.live.length - 1; i >= 0; i--) {
      const l = this.live[i];
      if (l.ttl > 0) {
        l.age += ms;
        const u = Math.min(1, l.age / l.ttl);
        const mat = l.head.material as THREE.MeshBasicMaterial;
        mat.opacity = 1 - u * u;
        if (l.beam) {
          this._p.copy(l.from).lerp(l.to, 0.5);
          this.seatStreak(l.head, this._p, l.dir, l.trailLen, l.trailW * (1 + u * 0.6), cam);
        } else {
          this.seatDot(l.head, l.from, l.size * (1 + u * 1.4), cam);
        }
        if (u >= 1) { mat.opacity = 1; this.live.splice(i, 1); this.release(l); }
        continue;
      }
      l.travelled += l.speed * dt;
      if (l.travelled >= l.dist) {
        // A MISS just runs out of flight: no burst, no sound, no mark on air.
        if (l.hits) {
          this.counters.impacts++;
          const color = (l.head.material as THREE.MeshBasicMaterial).color.getHex();
          this.spawnTimed('dot', color, l.to, BURST_M * (l.kind === 'shell' ? 1.6 : 1) * Math.max(0.8, l.size / HEAD_M), BURST_MS);
          l.onImpact?.();
        }
        this.live.splice(i, 1);
        this.release(l);
        continue;
      }
      this._p.copy(l.from).addScaledVector(l.dir, l.travelled);
      this.seatDot(l.head, this._p, l.size, cam);
      if (l.trail) {
        // the tail never reaches back behind the muzzle
        const len = Math.min(l.trailLen, Math.max(0.05, l.travelled + l.size * 0.5));
        this._p.addScaledVector(l.dir, -len * 0.5 + l.size * 0.25);
        this.seatStreak(l.trail, this._p, l.dir, len, l.trailW, cam);
      }
    }
    this.counters.live = this.live.length;
  }

  /** Probe diag: every live head's world position, size and screen position
   *  (NDC + the on-screen diameter in px for `viewportH`) through `cam`. */
  screenHeads(cam: THREE.Camera, viewportH: number): Array<{ x: number; y: number; z: number; size: number; ndcX: number; ndcY: number; px: number; kind: string }> {
    const out: Array<{ x: number; y: number; z: number; size: number; ndcX: number; ndcY: number; px: number; kind: string }> = [];
    const camPos = this.cameraPos();
    const pc = cam as THREE.PerspectiveCamera;
    const fovRad = ((pc.fov ?? 60) * Math.PI) / 180;
    for (const l of this.live) {
      // The LOCAL position is the absolute coordinate; the drawn position is
      // that plus the scene's rebase shift. Reporting the local one made this
      // diagnostic blind to a frame error — it agreed with a camera read in the
      // other frame and scored a bolt drawn kilometres away as perfectly placed.
      const p = l.head.position.clone();
      const d = p.distanceTo(camPos);
      // `project` maps a RENDER-space point through the camera, but `p` is the
      // absolute coordinate — add the scene's rebase shift back before
      // projecting or the reported NDC is wrong by a whole origin.
      const v = p.clone().add(this.scene.position).project(cam);
      out.push({ x: +p.x.toFixed(2), y: +p.y.toFixed(2), z: +p.z.toFixed(2), size: +l.size.toFixed(2), ndcX: +v.x.toFixed(3), ndcY: +v.y.toFixed(3), px: +((l.size / Math.max(0.05, d)) / (2 * Math.tan(fovRad / 2)) * viewportH).toFixed(1), kind: l.ttl > 0 ? (l.beam ? 'beam' : 'flash') : l.kind });
    }
    return out;
  }

  dispose(): void {
    for (const l of this.live) { this.root.remove(l.head); if (l.trail) this.root.remove(l.trail); }
    this.live.length = 0;
    this.root.parent?.remove(this.root);
    this.geo.dispose();
    for (const m of this.mats.values()) m.dispose();
    for (const m of this.ownMats) m.dispose();
    this.ownMats.clear();
    if (this.tex) { this.tex.dot.dispose(); this.tex.streak.dispose(); }
    this.mats.clear();
    this.free.clear();
  }
}
